import React, { useEffect, useMemo, useState } from "react";
import { ExternalLink } from "lucide-react";
import useAuth from "../../hooks/useAuth";
import { roleRoutes } from "../../utils/roles";
import { getUserIntegrations } from "../../api/integrations";
import {
  getActiveAnnouncements,
  sendAnnouncementReceipt,
} from "../../api/announcements";
import AnnouncementModal from "./AnnouncementModal";
import RemoteAnnouncementContent, { safeLinkUrl } from "./RemoteAnnouncementContent";
import { ANNOUNCEMENT_HOOKS } from "./registry";
import { isAnnouncementDismissed } from "./announcements.storage";

// ─────────────────────────────────────────────────────────────────────────────
// AnnouncementsHost — orquestador de los anuncios post-login (ver registry.js
// para la receta de cómo agregar uno nuevo).
//
// Dos fuentes:
//   1. Los anuncios del registro (escritos en el código, descarte en
//      localStorage). Tienen prioridad, igual que antes.
//   2. Los pop-ups que publican los operadores y Beartrack desde el panel
//      (`GET contracts/announcements/active`). El servidor ya los filtra por
//      audiencia y los ordena (nunca visto primero, después el visto hace más
//      tiempo), y guarda por usuario el visto / descartado / clic.
//
// Responsabilidades:
// - Construye UNA vez el contexto compartido que consumen las definiciones
//   (usuario, rol comercio, integraciones de la empresa) para que N anuncios
//   no disparen N fetches.
// - Llama los hooks del registro (arreglo estático → orden de hooks estable),
//   descarta los ya silenciados (localStorage por id) y muestra SOLO el
//   primero elegible por montaje del layout: nunca se apilan modales; si hay
//   varios pendientes, el siguiente aparece en el próximo login.
// - Si las fuentes fallan, no muestra nada: la sesión nunca depende de esto.
// ─────────────────────────────────────────────────────────────────────────────

/** Abre el destino del botón: https en otra pestaña, mailto/tel en la misma. */
function openCtaUrl(url) {
  const safe = safeLinkUrl(url);
  if (!safe) return;
  if (safe.startsWith("https:")) {
    window.open(safe, "_blank", "noopener,noreferrer");
  } else {
    window.location.href = safe;
  }
}

/** Pop-up del servidor → descriptor del AnnouncementModal. */
function toDescriptor(announcement) {
  const ctaUrl = safeLinkUrl(announcement.cta?.url);
  return {
    id: `remote:${announcement.id}`,
    title: announcement.title,
    steps: [{ body: <RemoteAnnouncementContent announcement={announcement} /> }],
    cta:
      announcement.cta && ctaUrl
        ? {
            label: announcement.cta.label,
            icon: <ExternalLink className="h-4 w-4" aria-hidden="true" />,
            onClick: () => {
              sendAnnouncementReceipt(announcement.id, "cta_clicked");
              openCtaUrl(ctaUrl);
            },
          }
        : undefined,
    onDismiss: () => {
      sendAnnouncementReceipt(announcement.id, "dismissed");
    },
  };
}

export default function AnnouncementsHost() {
  const { user } = useAuth();
  const isClient = user?.role === roleRoutes.client;

  const [integrations, setIntegrations] = useState([]);
  const [integrationsLoaded, setIntegrationsLoaded] = useState(false);
  const [remote, setRemote] = useState([]);
  const [remoteLoaded, setRemoteLoaded] = useState(false);
  // El modal se abre una sola vez por montaje: cerrar (con o sin descarte
  // persistente) no debe hacer aparecer al siguiente candidato en cadena.
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    if (!user?.id || !isClient) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await getUserIntegrations({ clientId: user.id });
        if (cancelled) return;
        if (data && !data.error && data.ok) {
          setIntegrations(data.integrations || []);
        }
      } catch {
        // Sin señal de integraciones no se molesta al usuario.
      } finally {
        if (!cancelled) setIntegrationsLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, isClient]);

  useEffect(() => {
    if (!user?.id || !isClient) return undefined;
    let cancelled = false;
    (async () => {
      // getActiveAnnouncements nunca rechaza: ante error devuelve [].
      const list = await getActiveAnnouncements();
      if (cancelled) return;
      setRemote(list);
      setRemoteLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, isClient]);

  const ctx = useMemo(
    () => ({ user, isClient, integrations, integrationsLoaded }),
    [user, isClient, integrations, integrationsLoaded],
  );

  // Registro ESTÁTICO: mismo número y orden de hooks en cada render.
  const candidates = ANNOUNCEMENT_HOOKS.map((useAnnouncement) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks -- arreglo estático (ver registry.js)
    useAnnouncement(ctx),
  );

  // El servidor ya los ordena: se toma el primero.
  const remoteFirst = remote[0] ?? null;
  const remoteDescriptor = useMemo(
    () => (remoteFirst ? toDescriptor(remoteFirst) : null),
    [remoteFirst],
  );

  // Derivación barata por render (arreglo de pocos anuncios): sin memo para
  // no fingir dependencias sobre `candidates`, que se recomputa cada render.
  const ready = isClient && integrationsLoaded && remoteLoaded && !closed;
  const staticActive = ready
    ? (candidates.find((a) => a && !isAnnouncementDismissed(a.id)) ?? null)
    : null;
  const active = ready ? (staticActive ?? remoteDescriptor) : null;

  // "Visto" del pop-up del servidor: una vez por montaje en que se muestra.
  const shownRemoteId =
    active && active === remoteDescriptor ? remoteFirst.id : null;
  useEffect(() => {
    if (shownRemoteId) sendAnnouncementReceipt(shownRemoteId, "seen");
  }, [shownRemoteId]);

  if (!active) return null;

  return (
    <AnnouncementModal
      announcement={active}
      open
      onClose={() => setClosed(true)}
    />
  );
}
